/**
 * Framer-based motion primitives for the landing page.
 *
 * `Reveal` is a drop-in replacement for the page's original inline
 * reveal-on-scroll wrapper — same `{ children, delay, style }` API, so existing
 * usages upgrade without edits — now with premium easing + a proper viewport
 * trigger. `Parallax` gives scroll-linked depth. `Tilt` gives a premium
 * hover lift/tilt. All honor reduced-motion (via the app-level MotionConfig
 * reducedMotion="user", plus explicit guards where scroll values are used).
 */

import {
  m,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionStyle,
} from "framer-motion";
import { useRef, type CSSProperties, type ReactNode } from "react";

// Brand --ease-out: cubic-bezier(0.2, 0.7, 0.2, 1).
const EASE = [0.2, 0.7, 0.2, 1] as const;

export function Reveal({
  children,
  delay = 0,
  y = 24,
  style,
}: {
  children: ReactNode;
  delay?: number; // milliseconds, matching the original API
  y?: number;
  style?: CSSProperties;
}) {
  return (
    <m.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2, margin: "0px 0px -10% 0px" }}
      transition={{ duration: 0.7, ease: EASE, delay: delay / 1000 }}
      style={style as MotionStyle}
    >
      {children}
    </m.div>
  );
}

export function Parallax({
  children,
  speed = 0.25,
  style,
}: {
  children: ReactNode;
  speed?: number; // 0 = static, 1 = strong
  style?: CSSProperties;
}) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const range = 70 * speed;
  const y = useTransform(scrollYProgress, [0, 1], [range, -range]);
  return (
    <m.div ref={ref} style={{ y: reduced ? 0 : y, ...(style as MotionStyle) }}>
      {children}
    </m.div>
  );
}

export function Tilt({
  children,
  style,
  lift = 6,
}: {
  children: ReactNode;
  style?: CSSProperties;
  lift?: number;
}) {
  const reduced = useReducedMotion();
  return (
    <m.div
      whileHover={reduced ? undefined : { y: -lift, rotateX: 2, rotateY: -2 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      style={{ transformPerspective: 900, ...(style as MotionStyle) }}
    >
      {children}
    </m.div>
  );
}
